import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SearchPrivateHiresResult } from "@/lib/coach-manager/client";
import type { PrivateHire } from "@/lib/coach-manager/types";
import { privateHireSearchKeys } from "@/lib/query";
import type { PrivateHireSearchRequest } from "@/schemas/private-hires";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * The current user's persisted last PrivateHire Search — one row per user
 * (see `private_hire_search_state`), superseded on every new search attempt
 * regardless of outcome.
 */
export interface PrivateHireSearchState {
  criteria: PrivateHireSearchRequest;
  status: "success" | "error";
  privateHires: PrivateHire[] | null;
  errorMessage: string | null;
  searchedAt: string;
}

/**
 * Search Coach Manager for PrivateHires via a saved BU instance. Mirrors
 * `useSearchContracts`'s shape — a `useMutation` with no cache invalidation
 * (read-only probe, not a write). Never throws on a Coach-Manager-level
 * failure (`{ ok: false, error }` is a valid result the caller renders
 * inline); only throws on a transport/validation failure of the route itself
 * (401/400/404).
 */
export function useSearchPrivateHires() {
  return useMutation({
    mutationFn: async (input: PrivateHireSearchRequest): Promise<SearchPrivateHiresResult> => {
      const res = await fetch("/api/coach-manager/private-hires/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "PrivateHire search failed"));
      return res.json();
    },
  });
}

/** The persisted last PrivateHire Search for the current user, or `null` if none. */
export function useLastPrivateHireSearch() {
  return useQuery({
    queryKey: privateHireSearchKeys.last,
    queryFn: async (): Promise<PrivateHireSearchState | null> => {
      const res = await fetch("/api/coach-manager/private-hires/search/last");
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to load last search"));
      const json = await res.json();
      return json.state;
    },
  });
}

/** Clears the persisted last PrivateHire Search for the current user. */
export function useClearLastPrivateHireSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/coach-manager/private-hires/search/last", {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to clear search"));
    },
    onSuccess: () => {
      queryClient.setQueryData(privateHireSearchKeys.last, null);
    },
  });
}
